import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';

import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { markDeletedItems } from 'utils/markDeletedItems';

import { ComplexRoleService } from 'components/services/ComplexRoleService';
import ConfirmationDialog from 'ui/shared/ConfirmationDialog';

import { ComplexRoleUI, TreeNode } from '../../types/interfaces';
import { DropdownSelectorType } from '../../types/interfaces/DropdownSelectorType';
import { PickerTexts } from '../../utils';
import { CustomDropdown } from '../custom-controls';
import { TagsComponent } from '../TagsComponent';

interface RefInterface {
  handleCancel: () => void;
}

interface DialogContentProps {
  userRole: ComplexRoleUI[];
  inheritedRoles: ComplexRoleUI[];
  inheritedRoleTags: DropdownSelectorType[];
  roleTags: TreeNode[];
  rolesDepartments: TreeNode[];
  departments: TreeNode[];
  handleDistrictSelectionChange: (e: TreeNode | TreeNode[]) => void;
  onSelectionChange: (selection: ComplexRoleUI[]) => void;
  errors: any;
  closeDialog?: () => void;
}

const AccessDialogContent = React.forwardRef<
  RefInterface | null,
  DialogContentProps
>(
  (
    {
      userRole,
      inheritedRoles,
      inheritedRoleTags,
      roleTags,
      rolesDepartments,
      departments,
      handleDistrictSelectionChange,
      onSelectionChange,
      errors,
      closeDialog
    }: DialogContentProps,
    ref
  ) => {
    const [localUserRole, setLocalUserRole] = useState<ComplexRoleUI[]>([]);
    const [localUserRoleTags, setLocalUserRoleTags] = useState<
      DropdownSelectorType[]
    >([]);
    const [localRolesDepartments, setLocalRolesDepartments] = useState<
      TreeNode[] | null
    >(null);
    const [unsavedChanges, setUnsavedChanges] = useState(false);
    const [showDialog, setShowDialog] = useState(false);
    const toast = useRef<Toast>(null);

    const [complexRoleOptions, setComplexRoleOptions] = useState<ComplexRoleUI[]>([]);

    useEffect(() => {
      setLocalUserRole(userRole);
      setLocalUserRoleTags(
        userRole.map((role) => ({
          id: typeof role.id === 'number' ? role.id.toString() : role.id,
          label: role.name
        }))
      );
      const fetchSchedules = async () => {
        const resultComplexRoleOptions = await ComplexRoleService.getComplexRolesForSelection();
        setComplexRoleOptions(resultComplexRoleOptions.data.value)
      };
      fetchSchedules()
      setLocalRolesDepartments(rolesDepartments);
    }, [userRole, rolesDepartments]);


    const handleSelectionChange = (selection: ComplexRoleUI | ComplexRoleUI[] | null) => {
      if (!selection) {
        // Handle null case
        setLocalUserRole([]);
        setLocalUserRoleTags([]);
        setUnsavedChanges(true);
        return;
      }

      const selectedArray = Array.isArray(selection) ? selection : [selection];
      
      // Update both localUserRole and localUserRoleTags when dropdown changes
      setLocalUserRole(selectedArray);
      setLocalUserRoleTags(
        selectedArray.map((role) => ({
          id: typeof role.id === 'number' ? role.id.toString() : role.id,
          label: role.name
        }))
      );
      setUnsavedChanges(true);
    };

    const handleDistrictChange = (selectedDistricts: TreeNode | TreeNode[]) => {
      setLocalRolesDepartments(() =>
        Array.isArray(selectedDistricts)
          ? selectedDistricts
          : [selectedDistricts]
      );
      setUnsavedChanges(true);
    };

    const onSubmit = () => {
      // No need to convert `id` since it is already a number
      onSelectionChange(localUserRole);
      localRolesDepartments &&
        handleDistrictSelectionChange(localRolesDepartments);
      setUnsavedChanges(false);
      closeDialog && closeDialog();
    };

    const handleCancel = () => {
      if (unsavedChanges) {
        setShowDialog(true);
      } else if (closeDialog) {
        closeDialog();
      }
    };

    useImperativeHandle(ref, () => ({
      handleCancel
    }));

    const handleConfirmationContinue = () => {
      setShowDialog(false);
      closeDialog && closeDialog();
    };

    const handleConfirmationCancel = () => {
      setShowDialog(false);
    };

    const handleDeleteRole = (taskId: string | number) => {
      setLocalUserRoleTags((prevUserRoleTags) =>
        prevUserRoleTags.filter((role) => role.id !== taskId)
      );
      // Also update localUserRole to keep it in sync
      setLocalUserRole((prevUserRole) =>
        prevUserRole.filter(
          (role) => role.id !== parseInt(taskId?.toString(), 10)
        )
      );
      setUnsavedChanges(true);
    };

    const handleDeleteRolesDepartments = (taskId: string | number) => {
      setLocalRolesDepartments(
        (prevRolesDepartments) =>
          prevRolesDepartments &&
          prevRolesDepartments.filter((node) => node.key !== taskId?.toString())
      );
      setUnsavedChanges(true);
    };

    return (
      <div className="access-dialog-content">
        <Toast ref={toast} />
        <div>
          <div className="flex flex-column md:flex-row gap-3">
            <div className="flex flex-column">
              <div className="flex flex-column">
                <div className="flex justify-content-between align-items-center gap-3">
                  <label htmlFor="userRole" style={{ minWidth: '53.33px' }}>
                    Роли:
                  </label>
                  <div style={{ width: '237px' }}>
                    <CustomDropdown
                      multiplySelectMode={true}
                      assetOptions={complexRoleOptions}
                      selectedAsset={localUserRole}
                      onSelectionChange={handleSelectionChange}
                      fieldName="name"
                      placeholder={localUserRoleTags.length === 0 ? 'Укажите роли' : PickerTexts.roles(localUserRoleTags.length)}
                      emptyMessage="Нет ролей для выбора."
                      minWidth={415}
                    />
                  </div>

                </div>
                <p
                  className={`employee-card-errors card-errors ${
                    errors.userRole ? 'text-red-500' : 'text-transparent'
                  }`}
                >
                  {/* {errors.userRole ? errors.userRole.message : ' '} */}
                </p>
              </div>
            </div>
          </div>
          {localUserRoleTags.length > 0 && (
            <div className="flex flex-column gap-3 mb-4">
              <div
                style={{ width: '100%', maxHeight: '136px', overflow: 'auto' }}
                id="tags-scroll"
              >
                <TagsComponent
                  initialTagsItems={markDeletedItems<DropdownSelectorType>(localUserRoleTags, complexRoleOptions) as any}
                  onDelete={handleDeleteRole}
                />
              </div>
            </div>
          )}

          {inheritedRoleTags.length > 0 && (
            <div className="flex flex-column gap-3 mb-3">
              <label htmlFor="userRole">Роли участков:</label>
              <div
                style={{ width: '100%', maxHeight: '160px', overflow: 'auto' }}
                id="tags-scroll"
              >
                <TagsComponent initialTagsItems={inheritedRoleTags} />
              </div>
            </div>
          )}

          <div className="flex flex-column md:flex-row gap-3">
            <div className="flex flex-column">
              <div className="flex flex-column">
                <div className="flex justify-content-between align-items-center gap-3">
                  <label htmlFor="userRole">Участки:</label>
                  <div style={{ width: '237px' }}>
                    <CustomDropdown
                      overlayClassName="dropdown-tree-hr_fisrtItem"
                      customHeight={300}
                      assetOptions={[]}
                      treeValue={departments}
                      useTree={true}
                      multiplySelectMode={true}
                      selectedAsset={localRolesDepartments}
                      onTreeSelectionChange={handleDistrictChange}
                      fixedLabel={PickerTexts.departments(localRolesDepartments?.length || 0)}
                      placeholder="Выбор участков"
                      overlayWidthMultiplier={3}
                    />
                  </div>
                </div>
                <p
                  className={`employee-card-errors card-errors ${
                    errors.userRole ? 'text-red-500' : 'text-transparent'
                  }`}
                >
                  {/* {errors.userRole ? errors.userRole.message : ' '} */}
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-column gap-3 mb-3">
            {localRolesDepartments?.length! > 0 && (
              <div
                style={{ width: '100%', maxHeight: '136px', overflow: 'auto' }}
                id="tags-scroll"
              >
                <TagsComponent
                  initialTagsItems={localRolesDepartments!}
                  onDelete={handleDeleteRolesDepartments}
                />
              </div>
            )}
            {(!localRolesDepartments || localRolesDepartments?.length! < 1) && (
              <div
                style={{ width: '100%', maxHeight: '75px', overflow: 'auto' }}
                id="tags-scroll"
              >
                Участки не назначены
              </div>
            )}
          </div>
        </div>
        <div>
          <div className="default-card__buttons">
            <div className="flex gap-3">
              <Button label="Принять" icon="pi pi-check" onClick={onSubmit} />
              <Button
                label="Отмена"
                icon="pi pi-times"
                onClick={handleCancel}
                className="p-button-secondary"
              />
            </div>
          </div>
        </div>

        <ConfirmationDialog
          visible={showDialog}
          onCancel={handleConfirmationCancel}
          onContinue={handleConfirmationContinue}
        />
      </div>
    );
  }
);

AccessDialogContent.displayName = 'AccessDialogContent';

export default AccessDialogContent;
